import {createElem} from 'common/util/dom';

interface LayoutConfig {
  bannerContainer?: HTMLElement;
  fullWidth?: boolean;
  hideGlobalActions?: boolean;
  whiteBackground?: boolean;
}

const DEFAULT_CONFIG: LayoutConfig = Object.freeze({
  bannerContainer: null,
  fullWidth: false,
  hideGlobalActions: false,
  whiteBackground: false,
});

/**
 * This class acts as a wrapper around methods for changing layout.
 * Certain apps (e.g dashboards) need to override things like max/min width and report-banners
 * based on their layout requirements. Be sure to test any changes in django AND volt mode.
 */
class LayoutManager {
  private currentConfig = {...DEFAULT_CONFIG};
  private updateConfig(config: Partial<LayoutConfig>) {
    Object.entries(config).forEach(([key, value]) => {
      if (key in DEFAULT_CONFIG) {
        this.currentConfig[key] = value;
      }
    });
  }

  public getConfig(): LayoutConfig {
    return this.currentConfig;
  }

  public setConfig(config: LayoutConfig) {
    this.updateConfig(config);
    /**
     * Set width; our ".width_wrapper" layout div imposes min/max width restrictions.
     * Certain reports can disable this by adding a ".full-width-report" class name.
     */
    if (Object.prototype.hasOwnProperty.call(config, `fullWidth`)) {
      const widthWrapper = document.querySelector(`#width_wrapper`);
      if (config.fullWidth) {
        widthWrapper?.classList.add(`full-width-report`);
      } else {
        widthWrapper?.classList.remove(`full-width-report`);
      }
    }

    /**
     * If config enabled, set the background to white. Allows to have in central location
     * rather than stray overrides
     */
    if (Object.prototype.hasOwnProperty.call(config, `whiteBackground`)) {
      const belowWrapper = document.querySelector(`.below_header`);
      if (config.whiteBackground) {
        belowWrapper?.classList.add(`white-background`);
      } else {
        belowWrapper?.classList.remove(`white-background`);
      }
    }

    if (Object.prototype.hasOwnProperty.call(config, `bannerContainer`)) {
      /**
       * Clear banners if in base template.
       * Previous bannerContainer will have been disconnected at this point
       **/
      const defaultBannerContainer = document.querySelector(`#messages`);
      if (defaultBannerContainer) {
        while (defaultBannerContainer.firstChild) {
          defaultBannerContainer.removeChild(defaultBannerContainer.firstChild);
        }
      }

      const {reportBanners, messageBanner} = this.createBanners();

      /**
       * If custom banner container is provided, use that. Else fallback to default
       * banner container. This gives apps more control over banner placement.
       */
      const bannerContainer = config.bannerContainer || defaultBannerContainer;

      if (bannerContainer && bannerContainer.prepend) {
        bannerContainer.prepend(messageBanner, reportBanners);
      }

      reportBanners.syncBanners?.();
    }

    /**
     * If a given report is using custom query report header sticky logic,
     * the global-report-actions should be hidden. The expectation is that
     * the sticky header will contain any functionality that was present in
     * the fixed global actions.
     */
    if (Object.prototype.hasOwnProperty.call(config, `hideGlobalActions`)) {
      const globalActionBar = document.querySelector(`global-action-bar`);
      if (globalActionBar) {
        if (config.hideGlobalActions) {
          requestAnimationFrame(() => globalActionBar.setAttribute(`hidden`, `hidden`));
        } else {
          requestAnimationFrame(() => globalActionBar.removeAttribute(`hidden`));
        }
      }
    }
  }

  /**
   * Reset to default config. Called by spa-nav-manager.ts
   */
  public reset() {
    this.setConfig(DEFAULT_CONFIG);
  }

  private createBanners() {
    return {
      reportBanners: createElem(`mp-report-banners`),
      messageBanner: createElem(`mp-report-message-banner`),
    };
  }
}

export const layoutManager = new LayoutManager();
